import type { ClientTarget } from '@features/clients';

import type { BulkModule, JobHeader } from '../schemas/bulk-migration';

/**
 * Where the screen is, held in the URL (`?module=&report=&view=`) so a reload,
 * a bookmark or a link in Slack reopens the same job at the same step.
 */

export type Step = 'pick-module' | 'analyze' | 'dry-run' | 'launch' | 'final-report';

/** `?view=final` on a launch: its final report instead of the live view. */
export const FINAL_REPORT_VIEW = 'final';

export function stepFor(
    moduleId: string | null,
    job: Pick<JobHeader, 'mode'> | undefined,
    reportId: string | null,
    view: string | null = null,
): Step {
    if (moduleId === null) return 'pick-module';
    if (reportId === null) return 'analyze';
    if (job === undefined) return 'dry-run';
    if (job.mode === 'dry-run') return 'dry-run';
    // Only a launch has a final report; a refresh is redirected to its launch
    // first, keeping the view, so a link to one still lands on the report.
    return view === FINAL_REPORT_VIEW && job.mode === 'launch' ? 'final-report' : 'launch';
}

/**
 * The link that opens a launch's final report, for sharing. Carries the target
 * the way the rest of the app does (`?env=&slot=`), so it opens the same
 * environment for whoever follows it, whatever theirs was.
 */
export function finalReportPath(
    target: ClientTarget,
    moduleId: string,
    launchReportId: string,
): string {
    const params = new URLSearchParams({ env: target.env });
    if (target.slot !== undefined) params.set('slot', target.slot);
    params.set('module', moduleId);
    params.set('report', launchReportId);
    params.set('view', FINAL_REPORT_VIEW);
    return `/migrations?${params.toString()}`;
}

/**
 * Which report to open for a job picked from the history: a refresh opens the
 * launch it refreshed, because the launch view is where its runs are read.
 */
export function reportToOpen(job: Pick<JobHeader, 'mode' | 'reportId' | 'fromReportId'>): string {
    return job.mode === 'report' && job.fromReportId !== null ? job.fromReportId : job.reportId;
}

/** The newest refresh of a launch, from a module's history. */
export function latestReportOf(
    jobs: readonly JobHeader[],
    launchReportId: string,
): JobHeader | undefined {
    return jobs
        .filter((job) => job.mode === 'report' && job.fromReportId === launchReportId)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/**
 * The newest refresh of a launch that FINISHED. The final report is built from
 * it rather than from {@link latestReportOf}: a refresh still writing its rows
 * would make the report read half a status.
 */
export function latestFinishedReportOf(
    jobs: readonly JobHeader[],
    launchReportId: string,
): JobHeader | undefined {
    return latestReportOf(
        jobs.filter((job) => job.state === 'done'),
        launchReportId,
    );
}

/**
 * Whether a launch's newest finished refresh could be missing from `jobs`, a
 * window of the module's newest jobs: none of it refreshes the launch, the
 * window is full, and even its oldest job is newer than the launch — so the
 * refreshes may all sit past its end. Listed newest first, the window always
 * holds the newest refresh when it holds any.
 */
export function refreshLookupLimited(
    jobs: readonly JobHeader[],
    launch: Pick<JobHeader, 'reportId' | 'createdAt'>,
    windowSize: number,
): boolean {
    if (jobs.length < windowSize) return false;
    if (latestFinishedReportOf(jobs, launch.reportId) !== undefined) return false;
    const oldest = jobs.reduce<string | null>(
        (min, job) => (min === null || job.createdAt < min ? job.createdAt : min),
        null,
    );
    return oldest !== null && oldest > launch.createdAt;
}

export interface ModuleGroup {
    readonly domain: string;
    readonly modules: readonly BulkModule[];
}

/** Modules grouped by domain, groups and modules in the runner's order. */
export function groupModules(modules: readonly BulkModule[]): ModuleGroup[] {
    const groups = new Map<string, BulkModule[]>();
    for (const module of [...modules].sort((a, b) => a.order - b.order)) {
        const group = groups.get(module.domain.title) ?? [];
        group.push(module);
        groups.set(module.domain.title, group);
    }
    return [...groups].map(([domain, list]) => ({ domain, modules: list }));
}

export const MODE_LABEL: Readonly<Record<JobHeader['mode'], string>> = {
    'dry-run': 'Analysis',
    launch: 'Migration',
    report: 'Status refresh',
};
